import { toMinHHMM } from './time.js';

// ═══════════════════════════════════════════════════════════════════
// HOW LATE A MEMBER MAY CANCEL THEIR OWN BOOKING (#1135, owner decision
// 2026-11-01 — the CTO's booking domain, a deliberate product rule).
//
// Andreas chose option B: a member may cancel a booking themselves, paid ones
// included, with a full refund — up to a deadline the CLUB sets in its booking
// settings, four hours before start unless it says otherwise. After the
// deadline the app says «For sent å avbestille — kontakt klubben».
//
// Pure and in the engine for the reason `resolveBookingWindowDays` is: the
// server decides (the member cancel command), the member app draws the button
// and the settings dialog shows the value, and three readers of one rule must
// not each write their own.
//
// WALL-CLOCK TIME AT THE VENUE. A booking's date and start are the venue's
// local date and time, and «now» is the venue's (`venueNow` on the server), so
// the deadline is the start's local time minus the hours, carried across
// midnight by calendar date. Across a DST change that is the wall clock's four
// hours, which is what a person at the club reads.
// ═══════════════════════════════════════════════════════════════════

/** The deadline when the club has set none: four hours before start. */
export const DEFAULT_CANCELLATION_DEADLINE_HOURS = 4;
/** The longest deadline a club may set: a week. */
export const MAX_CANCELLATION_DEADLINE_HOURS = 168;

/**
 * The club's cancellation deadline, in whole hours before start.
 *
 * Use this, never the raw `cancellationDeadlineHours` field. Missing, empty,
 * non-numeric or negative reads as the default; a fraction is rounded down;
 * anything above a week is a week. ZERO IS A REAL SETTING — «until the booking
 * starts» — unlike the booking window, where zero days is not a booking system.
 */
export function resolveCancellationDeadlineHours(
  config?: { cancellationDeadlineHours?: unknown } | null,
): number {
  const raw = config?.cancellationDeadlineHours;
  // `Number(null)` and `Number('')` are 0, which IS a setting here — so an
  // absent value has to be told apart before it is converted.
  if (raw === null || raw === undefined || raw === '') return DEFAULT_CANCELLATION_DEADLINE_HOURS;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_CANCELLATION_DEADLINE_HOURS;
  return Math.min(Math.floor(n), MAX_CANCELLATION_DEADLINE_HOURS);
}

/** A calendar date `days` away, as `YYYY-MM-DD`. Date-only arithmetic: no clock, no zone. */
function shiftDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** The last moment a cancellation is in time: the venue date and the minute of that day. */
export function cancellationCutoff(
  booking: { date: string; startTime: string },
  hours: number,
): { date: string; minute: number } {
  let minute = toMinHHMM(booking.startTime) - hours * 60;
  const days = Math.floor(minute / 1440);
  minute -= days * 1440;
  return { date: days === 0 ? booking.date : shiftDate(booking.date, days), minute };
}

/**
 * Is it still before the deadline? `now` is the venue's date and minute
 * (`venueNow`). AT the deadline is too late: «four hours before» means a
 * cancellation must arrive before the clock shows it.
 */
export function beforeCancellationCutoff(
  booking: { date: string; startTime: string },
  now: { todayISO: string; nowMin: number },
  hours: number,
): boolean {
  const cut = cancellationCutoff(booking, hours);
  if (now.todayISO !== cut.date) return now.todayISO < cut.date;
  return now.nowMin < cut.minute;
}
